import { WarningTriangleIcon } from "@xola/icons";
import { Alert, flash, Modal, numberFormat, SubmitButton } from "@xola/ui-kit";
import { isOtherSellerPurchase } from "common";
import { findLast } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import { LineItemObject, LineItemStatus, LineItemType, PaymentMethod, Purchase, SellerPaymentMethod } from "types";
import { api } from "../../api/api";
import { useSeller } from "../../hooks/useAccount";
import { updateCache } from "../../utils/updateCache";
import { Currency } from "../Common/Currency";
import { SecondaryButton } from "../Common/SecondaryButton";
import { PaymentMethodButtons } from "./PaymentMethodButtons";

interface PurchaseManualRefundModalProps {
    isOpen: boolean;
    amountToRefund: number;
    purchase: Purchase;
    onClose: () => void;
}

export const PurchaseManualRefundModal = ({
    isOpen,
    amountToRefund,
    purchase,
    onClose,
}: PurchaseManualRefundModalProps) => {
    return (
        <Modal size="medium" isOpen={isOpen} onClose={onClose}>
            <ModalContent amountToRefund={amountToRefund} purchase={purchase} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ purchase, amountToRefund, onClose }: Except<PurchaseManualRefundModalProps, "isOpen">) => {
    const seller = useSeller();

    const [paymentMethod, setPaymentMethod] = useState<SellerPaymentMethod>();

    const { mutateAsync: returnPayment, isLoading: isUpdating } = useMutation(
        async (payload: ReturnType<typeof getPayload>) => {
            const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}`, payload);
            return data;
        },
    );

    const lastRefundAmount =
        findLast(purchase.lineItems, (item) => {
            return item.type === LineItemType.REFUND && item.status === LineItemStatus.COMPLETE;
        })?.amount ?? 0;
    const balanceToRefund = amountToRefund - lastRefundAmount;

    const handleSubmit = async () => {
        if (!paymentMethod) {
            return;
        }

        try {
            const updatedPurchase = await returnPayment(getPayload(paymentMethod, balanceToRefund));
            onClose();
            flash.success(`Refund of ${numberFormat(balanceToRefund, seller.currency)} applied`);
            updateCache("purchases", updatedPurchase);
        } catch {
            flash.danger("Error returning payment");
        }
    };

    const handlePaymentMethodChange = (value: SellerPaymentMethod) => {
        setPaymentMethod(value);
    };

    return (
        <>
            <Modal.Header>Manual Refund Required</Modal.Header>

            <Modal.Body>
                <div className="flex flex-wrap">
                    <div className="flex-1 space-y-4">
                        <Alert className="flex-0" color="warning">
                            <div className="alert-content-wrapper flex justify-between">
                                <WarningTriangleIcon size="large" className="mr-5 self-center" />
                                <p className="flex-1">
                                    We were only able to refund <Currency>{Math.abs(lastRefundAmount)}</Currency> to the
                                    customer&apos;s credit card. A balance of <Currency>{balanceToRefund}</Currency> is
                                    pending on this refund.
                                </p>
                            </div>
                        </Alert>

                        <PaymentMethodButtons
                            isRefund
                            value={paymentMethod}
                            excludePaymentMethods={[PaymentMethod.STORE_CREDIT, PaymentMethod.CREDIT_CARD]}
                            isOtherSellerPurchase={isOtherSellerPurchase(purchase, seller.id)}
                            onChange={handlePaymentMethodChange}
                        />

                        <p>
                            If you proceed with the manual refund (recommended), Xola will send a refund email to the
                            customer, however it is your responsibility to actually send the refund money.
                        </p>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="flex justify-end space-x-4">
                <SecondaryButton onClick={onClose}>Later</SecondaryButton>

                <SubmitButton disabled={!paymentMethod} isLoading={isUpdating} onClick={handleSubmit}>
                    Manually refund <Currency>{balanceToRefund}</Currency>
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};

const getPayload = (paymentMethod: SellerPaymentMethod, amount: number) => {
    const ops: any[] = [
        {
            path: "/lineItems",
            op: "addLineItem",
            data: {
                amount,
                name: "Return payment",
                object: LineItemObject.REFUND,
                payload: LineItemType.REFUND,
                payment: { method: paymentMethod.id, card: paymentMethod.card },
            },
        },
    ];

    return {
        ops,
    };
};
